// react-magnetic-di - temporary comment, for auto dependency injection in tests
import { useEffect, useMemo, useRef } from 'react';

import {
	type AnalyticsEventPayload,
	type UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlaskit/analytics-next';
import { fireOperationalEvent } from '@atlassian/ui-modifications-analytics';

import type { ScreenTabsDataPublic } from '../../../../../../../common/types/screen-tabs';
import { useReadyPublicDataForOnInit } from '../../../../../../../controllers/issue-adjustments/hooks/use-ready-fields';
import { useViewType } from '../../../../../../../controllers/issue-adjustments/hooks/use-view-type';
import { useIssueAdjustmentsActions } from '../../../../../../../controllers/issue-adjustments/main';
import { getAnalytics } from '../../../../../../../controllers/view-configuration';
import { filterFormDataBasedOnScopes } from '../utils/filter-based-on-scopes/form-data';
import { filterScreenTabsBasedOnReadScopes } from '../utils/filter-based-on-scopes/screen-tabs-data';

import type { OnInitPayload, ResolveOnInitPromise } from './types';

export type UseOnInitResult = {
	onInitPromise: Promise<OnInitPayload>;
};

type UseOnInitProps = {
	moduleScopes: readonly string[];
};

const successTabsInitialisation = (
	createAnalyticsEvent: (_payload: AnalyticsEventPayload) => UIAnalyticsEvent,
	viewType: string,
	screenTabsData: ScreenTabsDataPublic,
) => {
	if (screenTabsData.length) {
		fireOperationalEvent(createAnalyticsEvent, {
			action: 'success',
			actionSubject: 'jiraUiModifications.screenTabsInitialization',
			attributes: {
				initializedTabsCount: screenTabsData.length,
				viewType,
			},
		});
	}
};

export const useOnInit = ({ moduleScopes }: UseOnInitProps): UseOnInitResult => {
	const resolveOnInitPromise = useRef<ResolveOnInitPromise | null>(null);
	const viewType = useViewType();
	const publicUIMDataForApp = useReadyPublicDataForOnInit({ viewType });

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { successFieldsInitalisation } = useIssueAdjustmentsActions();

	const onInitPromise = useMemo(() => {
		const promise = new Promise<OnInitPayload>(
			(resolve: (result: Promise<OnInitPayload> | OnInitPayload) => void) => {
				resolveOnInitPromise.current = resolve;
			},
		);

		promise.then(() => {
			// It cleans the resolveOnInitPromise to avoid calling the body
			// of the below useEffect after the onInit being resolved once.
			resolveOnInitPromise.current = null;
		});

		return promise;
	}, []);

	useEffect(() => {
		const handleChange = resolveOnInitPromise.current;

		if (publicUIMDataForApp === null || handleChange === null) {
			return;
		}

		const payload: OnInitPayload = {
			currentFormState: filterFormDataBasedOnScopes(
				publicUIMDataForApp.formData,
				viewType,
				moduleScopes,
			),
			currentScreenTabsState: filterScreenTabsBasedOnReadScopes(
				moduleScopes,
				publicUIMDataForApp.screenTabsData,
			),
		};
		handleChange(payload);
		successFieldsInitalisation({ createAnalyticsEvent });
		fireOperationalEvent(createAnalyticsEvent, {
			action: 'success',
			actionSubject: 'jiraUiModifications.appNotification',
			attributes: {
				lifecycleEvent: 'init',
				viewType,
			},
		});

		successTabsInitialisation(createAnalyticsEvent, viewType, publicUIMDataForApp.screenTabsData);

		getAnalytics(viewType).markOnInitResolved();
	}, [
		successFieldsInitalisation,
		createAnalyticsEvent,
		publicUIMDataForApp,
		moduleScopes,
		viewType,
	]);

	return { onInitPromise };
};
